
import React from "react";

import {
  Document,
  Page,
  View,
  Text,
  StyleSheet,
} from "@react-pdf/renderer";


const styles = StyleSheet.create({
  page: {
    paddingTop: 32,
    paddingBottom: 42,
    paddingHorizontal: 30,
    fontFamily: "Helvetica",
    fontSize: 8,
    color: "#243247",
    backgroundColor: "#FFFFFF",
  },

  header: {
    backgroundColor: "#082B4C",
    paddingVertical: 18,
    paddingHorizontal: 18,
    borderRadius: 5,
    marginBottom: 16,
  },

  schoolName: {
    color: "#F5C451",
    fontSize: 9,
    fontWeight: "bold",
    letterSpacing: 1.2,
    marginBottom: 6,
    textAlign: "center",
  },

  title: {
    color: "#FFFFFF",
    fontSize: 19,
    fontWeight: "bold",
    textAlign: "center",
    letterSpacing: 0.5,
  },

  subtitle: {
    color: "#DCE7F1",
    fontSize: 9,
    marginTop: 6,
    textAlign: "center",
  },

  metaRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 14,
    paddingBottom: 9,
    borderBottomWidth: 1,
    borderBottomColor: "#DCE3EA",
  },

  metaItem: {
    flexDirection: "column",
    gap: 3,
  },

  metaLabel: {
    color: "#718096",
    fontSize: 7,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },

  metaValue: {
    color: "#1C2F43",
    fontSize: 9,
    fontWeight: "bold",
  },

  summary: {
    flexDirection: "row",
    gap: 8,
    marginBottom: 17,
  },

  summaryCard: {
    flexGrow: 1,
    flexBasis: 0,
    padding: 10,
    borderWidth: 1,
    borderColor: "#DCE5EE",
    borderRadius: 4,
    backgroundColor: "#F7FAFC",
  },

  summaryLabel: {
    fontSize: 7,
    color: "#66788A",
    marginBottom: 5,
    textTransform: "uppercase",
  },

  summaryValue: {
    fontSize: 12,
    color: "#082B4C",
    fontWeight: "bold",
  },

  sectionTitle: {
    color: "#082B4C",
    fontSize: 10,
    fontWeight: "bold",
    marginBottom: 8,
  },

  table: {
    width: "100%",
    borderWidth: 1,
    borderColor: "#DCE3EA",
    borderBottomWidth: 0,
  },

  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#082B4C",
    paddingVertical: 8,
    paddingHorizontal: 5,
    alignItems: "center",
  },

  tableRow: {
    flexDirection: "row",
    paddingVertical: 7,
    paddingHorizontal: 5,
    borderBottomWidth: 1,
    borderBottomColor: "#E5EAF0",
    alignItems: "center",
    minHeight: 27,
  },

  alternateRow: {
    backgroundColor: "#F7F9FC",
  },

  headerText: {
    color: "#FFFFFF",
    fontSize: 6.5,
    fontWeight: "bold",
  },

  cell: {
    fontSize: 7,
    color: "#334155",
  },

  colNumber: {
    width: "4%",
  },

  colStaff: {
    width: "20%",
  },

  colType: {
    width: "11%",
  },

  colAmount: {
    width: "12%",
    textAlign: "right",
  },

  colDeducted: {
    width: "12%",
    textAlign: "right",
  },

  colBalance: {
    width: "12%",
    textAlign: "right",
  },

  colStatus: {
    width: "12%",
    textAlign: "center",
  },

  colReason: {
    width: "17%",
    paddingLeft: 5,
  },

  totalsRow: {
    flexDirection: "row",
    backgroundColor: "#EAF1F7",
    paddingVertical: 9,
    paddingHorizontal: 5,
    borderBottomWidth: 1,
    borderBottomColor: "#DCE3EA",
  },

  totalsLabel: {
    width: "35%",
    color: "#082B4C",
    fontSize: 8,
    fontWeight: "bold",
  },

  totalsValue: {
    width: "21%",
    color: "#082B4C",
    fontSize: 8,
    fontWeight: "bold",
    textAlign: "right",
  },

  note: {
    marginTop: 13,
    padding: 9,
    backgroundColor: "#F7FAFC",
    borderLeftWidth: 3,
    borderLeftColor: "#D5A52E",
  },

  noteText: {
    color: "#64748B",
    fontSize: 7,
    lineHeight: 1.5,
  },

  empty: {
    padding: 22,
    textAlign: "center",
    color: "#718096",
    fontSize: 9,
  },

  footer: {
    position: "absolute",
    left: 30,
    right: 30,
    bottom: 18,
    paddingTop: 7,
    borderTopWidth: 1,
    borderTopColor: "#DCE3EA",
    flexDirection: "row",
    justifyContent: "space-between",
  },

  footerText: {
    fontSize: 7,
    color: "#8794A3",
  },
});


// ============================================
// HELPER FUNCTIONS
// ============================================

const toNumber = (value) => {
  const number = Number(value);

  return Number.isFinite(number) ? number : 0;
};


const money = (value) => {
  return `$${toNumber(value).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};


const getStaffName = (item) => {
  return (
    item?.teacher_name ||
    item?.support_staff_name ||
    "Unspecified staff member"
  );
};


const getStaffType = (item) => {
  if (item?.teacher_name || item?.teacher) {
    return "Teacher";
  }

  if (item?.support_staff_name || item?.support_staff) {
    return "Support Staff";
  }

  return "Unspecified";
};


const getStatus = (item) => {
  if (item?.status) {
    return String(item.status);
  }

  return toNumber(item?.remaining_balance) > 0
    ? "Pending"
    : "Fully Taken";
};


// ============================================
// SALARY ADVANCE PDF COMPONENT
// ============================================

const SalaryAdvancePDF = ({
  records = [],
  month = "",
  academicYear = "",
  generatedAt = "",
}) => {

  const safeRecords = Array.isArray(records)
    ? records
    : [];


  // Calculate monthly totals.
  const totals = safeRecords.reduce(
    (result, item) => {
      result.advance += toNumber(item?.amount);

      result.deducted += toNumber(
        item?.total_deducted
      );

      result.balance += toNumber(
        item?.remaining_balance
      );

      return result;
    },
    {
      advance: 0,
      deducted: 0,
      balance: 0,
    }
  );


  return (
    <Document
      title={`Salary Advance Report - ${month} - ${academicYear}`}
      author="School Management System"
      subject="Monthly Salary Advance Records"
    >

      <Page
        size="A4"
        orientation="landscape"
        style={styles.page}
      >

        {/* ================================= */}
        {/* REPORT HEADER */}
        {/* ================================= */}

        <View style={styles.header}>

          <Text style={styles.schoolName}>
            OFFICIAL FINANCIAL RECORD
          </Text>

          <Text style={styles.title}>
            SALARY ADVANCE REPORT
          </Text>

          <Text style={styles.subtitle}>
            Monthly Staff Salary Advance Summary
          </Text>

        </View>


        {/* ================================= */}
        {/* REPORT INFORMATION */}
        {/* ================================= */}

        <View style={styles.metaRow}>

          <View style={styles.metaItem}>
            <Text style={styles.metaLabel}>
              Reporting Month
            </Text>

            <Text style={styles.metaValue}>
              {month || "-"}
            </Text>
          </View>


          <View style={styles.metaItem}>
            <Text style={styles.metaLabel}>
              Academic Year
            </Text>

            <Text style={styles.metaValue}>
              {academicYear || "-"}
            </Text>
          </View>


          <View style={styles.metaItem}>
            <Text style={styles.metaLabel}>
              Total Records
            </Text>

            <Text style={styles.metaValue}>
              {safeRecords.length}
            </Text>
          </View>


          <View style={styles.metaItem}>
            <Text style={styles.metaLabel}>
              Generated
            </Text>

            <Text style={styles.metaValue}>
              {generatedAt || "-"}
            </Text>
          </View>

        </View>


        {/* ================================= */}
        {/* FINANCIAL SUMMARY */}
        {/* ================================= */}

        <View style={styles.summary}>

          <View style={styles.summaryCard}>
            <Text style={styles.summaryLabel}>
              Total Advances
            </Text>

            <Text style={styles.summaryValue}>
              {money(totals.advance)}
            </Text>
          </View>


          <View style={styles.summaryCard}>
            <Text style={styles.summaryLabel}>
              Total Amount Taken
            </Text>

            <Text style={styles.summaryValue}>
              {money(totals.deducted)}
            </Text>
          </View>


          <View style={styles.summaryCard}>
            <Text style={styles.summaryLabel}>
              Outstanding Balance
            </Text>

            <Text style={styles.summaryValue}>
              {money(totals.balance)}
            </Text>
          </View>

        </View>


        {/* ================================= */}
        {/* ADVANCE DETAILS */}
        {/* ================================= */}

        <Text style={styles.sectionTitle}>
          Salary Advance Details
        </Text>


        {safeRecords.length === 0 ? (

          <Text style={styles.empty}>
            No salary advance records were found for this month.
          </Text>

        ) : (

          <View style={styles.table}>

            {/* TABLE HEADER */}

            <View
              style={styles.tableHeader}
              fixed
            >

              <Text
                style={[
                  styles.headerText,
                  styles.colNumber,
                ]}
              >
                #
              </Text>


              <Text
                style={[
                  styles.headerText,
                  styles.colStaff,
                ]}
              >
                Staff Name
              </Text>


              <Text
                style={[
                  styles.headerText,
                  styles.colType,
                ]}
              >
                Staff Type
              </Text>


              <Text
                style={[
                  styles.headerText,
                  styles.colAmount,
                ]}
              >
                Advance
              </Text>


              <Text
                style={[
                  styles.headerText,
                  styles.colDeducted,
                ]}
              >
                Taken
              </Text>


              <Text
                style={[
                  styles.headerText,
                  styles.colBalance,
                ]}
              >
                Balance
              </Text>


              <Text
                style={[
                  styles.headerText,
                  styles.colStatus,
                ]}
              >
                Status
              </Text>


              <Text
                style={[
                  styles.headerText,
                  styles.colReason,
                ]}
              >
                Reason
              </Text>

            </View>


            {/* TABLE BODY */}

            {safeRecords.map((item, index) => (

              <View
                key={item?.id ?? `advance-${index}`}
                style={[
                  styles.tableRow,
                  index % 2 === 1
                    ? styles.alternateRow
                    : {},
                ]}
                wrap={false}
              >

                <Text
                  style={[
                    styles.cell,
                    styles.colNumber,
                  ]}
                >
                  {index + 1}
                </Text>


                <Text
                  style={[
                    styles.cell,
                    styles.colStaff,
                  ]}
                >
                  {getStaffName(item)}
                </Text>


                <Text
                  style={[
                    styles.cell,
                    styles.colType,
                  ]}
                >
                  {getStaffType(item)}
                </Text>


                <Text
                  style={[
                    styles.cell,
                    styles.colAmount,
                  ]}
                >
                  {money(item?.amount)}
                </Text>


                <Text
                  style={[
                    styles.cell,
                    styles.colDeducted,
                  ]}
                >
                  {money(item?.total_deducted)}
                </Text>


                <Text
                  style={[
                    styles.cell,
                    styles.colBalance,
                  ]}
                >
                  {money(item?.remaining_balance)}
                </Text>


                <Text
                  style={[
                    styles.cell,
                    styles.colStatus,
                  ]}
                >
                  {getStatus(item)}
                </Text>


                <Text
                  style={[
                    styles.cell,
                    styles.colReason,
                  ]}
                >
                  {item?.reason || "-"}
                </Text>

              </View>

            ))}


            {/* MONTHLY TOTAL */}

            <View
              style={styles.totalsRow}
              wrap={false}
            >

              <Text style={styles.totalsLabel}>
                MONTHLY TOTAL
              </Text>


              <Text style={styles.totalsValue}>
                {money(totals.advance)}
              </Text>


              <Text style={styles.totalsValue}>
                {money(totals.deducted)}
              </Text>


              <Text style={styles.totalsValue}>
                {money(totals.balance)}
              </Text>

            </View>

          </View>

        )}


        {/* ================================= */}
        {/* REPORT NOTE */}
        {/* ================================= */}

        <View style={styles.note}>

          <Text style={styles.noteText}>
            This report contains salary advance records for the
            selected month and academic year. Amounts are presented
            in US Dollars (USD). Please verify the records against
            the official payroll and deduction records before
            making financial decisions.
          </Text>

        </View>


        {/* ================================= */}
        {/* FOOTER */}
        {/* ================================= */}

        <View
          style={styles.footer}
          fixed
        >

          <Text style={styles.footerText}>
            OFFICIAL SALARY ADVANCE RECORD
          </Text>


          <Text
            style={styles.footerText}
            render={({ pageNumber, totalPages }) =>
              `Page ${pageNumber} of ${totalPages}`
            }
          />

        </View>

      </Page>

    </Document>
  );
};


export default SalaryAdvancePDF;
